import IconMetrics from '@apollo/icons/default/IconMetrics.svg';
import IconSingleService from '@apollo/icons/default/IconSingleService.svg';
import { Button } from '@apollo/orbit';
import React, { useCallback, useMemo } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';

import { FilterButton } from 'src/components/metricsFilter/filterButton/FilterButton';
import { MultiSelect } from 'src/components/multiSelect/MultiSelect';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import {
  graphProposalListRouteConfig,
  variantProposalListRouteConfig,
} from '../../routes';

export const ProposalsFilterToggles = ({
  className,
  allSubgraphs: allSubgraphsUnsorted,
  allVariants,
}: {
  className?: string;
  allSubgraphs: string[];
  allVariants: string[];
}) => {
  const {
    filterByStatuses,
    filterByVariants,
    filterBySubgraphs,
    view = 'graph',
  } = useRouteParams(
    graphProposalListRouteConfig,
    variantProposalListRouteConfig,
  );
  const isGraphProposalList = !!useRouteMatch(
    graphProposalListRouteConfig.definition,
  );

  const history = useHistory();
  const location = useLocation();

  const allSubgraphs = useMemo(
    () => allSubgraphsUnsorted.slice().sort((a, b) => a.localeCompare(b)),
    [allSubgraphsUnsorted],
  );

  const selectedSubgraphs = useMemo(
    () => new Set(filterBySubgraphs ?? []),
    [filterBySubgraphs],
  );
  const selectedVariants = useMemo(
    () => new Set(filterByVariants ?? []),
    [filterByVariants],
  );
  const selectedStatuses = useMemo(
    () => new Set(filterByStatuses ?? []),
    [filterByStatuses],
  );

  const hasActiveFilters =
    isMinLengthArray(1, filterBySubgraphs) ||
    isMinLengthArray(1, filterByStatuses);

  const pageHasFilters = allSubgraphs.length > 0;

  const setSelectedSubgraphs = useCallback(
    (values: Set<string>) =>
      history.push(
        isGraphProposalList
          ? graphProposalListRouteConfig.locationFrom({
              location,
              patch: {
                filterBySubgraphs: Array.from(values),
                page: RouteConfig.DELETE,
              },
            })
          : variantProposalListRouteConfig.locationFrom({
              location,
              patch: {
                filterBySubgraphs: Array.from(values),
                page: RouteConfig.DELETE,
              },
            }),
      ),
    [history, isGraphProposalList, location],
  );

  const setSelectedVariants = useCallback(
    (values: Set<string>) =>
      history.push(
        isGraphProposalList
          ? graphProposalListRouteConfig.locationFrom({
              location,
              patch: {
                filterByVariants: Array.from(values),
                page: RouteConfig.DELETE,
              },
            })
          : variantProposalListRouteConfig.locationFrom({
              location,
              patch: {
                filterByVariants: Array.from(values),
                page: RouteConfig.DELETE,
              },
            }),
      ),
    [history, isGraphProposalList, location],
  );

  const statusOptions = new Map([
    [GraphQLTypes.ProposalStatus.OPEN, 'Open'],
    [GraphQLTypes.ProposalStatus.APPROVED, 'Approved'],
    [GraphQLTypes.ProposalStatus.CLOSED, 'Closed'],
    [GraphQLTypes.ProposalStatus.DRAFT, 'Draft'],
    [GraphQLTypes.ProposalStatus.IMPLEMENTED, 'Implemented'],
  ]);

  return (
    <div className={className}>
      <MultiSelect
        valueTitle="Status"
        allValues={Array.from(statusOptions.keys())}
        selectedValues={selectedStatuses}
        setSelectedValues={(selectedValues) => {
          history.push(
            isGraphProposalList
              ? graphProposalListRouteConfig.locationFrom({
                  location,
                  patch: {
                    // As we only have 1 option in here, mimic the behavior of a
                    // radio button here rather than a multi-select.
                    filterByStatuses: Array.from(selectedValues.values()),
                    page: RouteConfig.DELETE,
                  },
                })
              : variantProposalListRouteConfig.locationFrom({
                  location,
                  patch: {
                    // As we only have 1 option in here, mimic the behavior of a
                    // radio button here rather than a multi-select.
                    filterByStatuses: Array.from(selectedValues.values()),
                    page: RouteConfig.DELETE,
                  },
                }),
          );
        }}
        triggerNode={
          <FilterButton variant="secondary" leftIcon={<IconMetrics />}>
            {selectedStatuses.size > 0
              ? `${selectedStatuses.size} ${format.pluralize(
                  selectedStatuses.size,
                  'Status',
                  'es',
                )}`
              : 'All Statuses'}
          </FilterButton>
        }
        labelForValue={(value) => statusOptions.get(value) ?? value}
      />
      {allSubgraphs.length > 0 && (
        <MultiSelect
          className="max-h-120 overflow-y-auto"
          valueTitle="Subgraph"
          allValues={allSubgraphs}
          canSearch={true}
          selectedValues={selectedSubgraphs}
          setSelectedValues={setSelectedSubgraphs}
          triggerNode={
            <FilterButton variant="secondary" leftIcon={<IconSingleService />}>
              {selectedSubgraphs.size > 0
                ? `${selectedSubgraphs.size} ${format.pluralize(
                    selectedSubgraphs.size,
                    'Subgraph',
                  )}`
                : 'All Subgraphs'}
            </FilterButton>
          }
        />
      )}
      {allVariants.length > 0 && view === 'graph' && (
        <MultiSelect
          className="max-h-120 overflow-y-auto"
          valueTitle="Source Variant"
          allValues={allVariants}
          canSearch={true}
          selectedValues={selectedVariants}
          setSelectedValues={setSelectedVariants}
          triggerNode={
            <FilterButton variant="secondary">
              {selectedVariants.size > 0
                ? `${selectedVariants.size} ${format.pluralize(
                    selectedVariants.size,
                    'Variant',
                  )}`
                : 'All Source Variants'}
            </FilterButton>
          }
        />
      )}
      {pageHasFilters && (
        <Button
          size="sm"
          variant="primary"
          isDisabled={!hasActiveFilters}
          as={Link}
          to={
            isGraphProposalList
              ? graphProposalListRouteConfig.locationFrom({
                  location,
                  patch: {
                    filterBySubgraphs: RouteConfig.DELETE,
                    filterByVariants: RouteConfig.DELETE,
                    filterByStatuses: RouteConfig.DELETE,
                    page: RouteConfig.DELETE,
                  },
                })
              : variantProposalListRouteConfig.locationFrom({
                  location,
                  patch: {
                    filterBySubgraphs: RouteConfig.DELETE,
                    filterByVariants: RouteConfig.DELETE,
                    filterByStatuses: RouteConfig.DELETE,
                    page: RouteConfig.DELETE,
                  },
                })
          }
        >
          Reset Filters
        </Button>
      )}
    </div>
  );
};
